import {
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  EmbeddedViewRef,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import { SpinnerComponent } from '@app/ui-components/loading-spinner/spinner/spinner.component';

/**
 * Adds a loading spinner to a button.
 *
 * Make sure to prefix the directive's name with an asterisk (*) (i.e. *appBtnLoading)
 *
 * This directive can **only** be applied to buttons.
 * Also, the buttons must not be absolutely positioned.
 *
 * Assign a variable/value to the directive (`isLoadingSetPW` in the example) to show/hide the spinner.
 *
 * Example:
 * ```html
 * <button *appBtnLoading="isLoadingSetPW">Click me!</button>
 * ```
 *
 * Note:
 *
 * If you want to apply another structural directive like `*ngIf` on the same button,
 * you must wrap the button in an ng-container on which you apply the `*ngIf`.
 *
 * Example:
 * ```html
 * <ng-container *ngIf="condition">
 *   <button *appBtnLoading="isLoadingSetPW">Click me!</button>
 * </ng-container>
 * ```
 */
@Directive({
  selector: '[appBtnLoading]'
})
export class BtnLoadingDirective implements OnInit, OnChanges {
  private spinnerVisible = false;
  private view: EmbeddedViewRef<any>;
  private spinnerInstance: ComponentRef<SpinnerComponent>;

  /**
   * Initial styles of the templateRef root.
   * These need to be saved before they are overwritten to show the spinner.
   * If a property is undefined, it has no initial value.
   */
  private initialStyles: InitialStyles = {};

  /**
   * Shows (true)/Hides (false) the spinner
   */
  @Input('appBtnLoading') public isLoading = false;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainerRef: ViewContainerRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private renderer: Renderer2
  ) { }

  public ngOnInit() {
    // Show button by default
    this.view = this.viewContainerRef.createEmbeddedView(this.templateRef);

    if (this.isLoading) {
      this.showSpinner();
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.isLoading && this.view) {
      // Only show spinner if view is already initialized
      this.isLoading ? this.showSpinner() : this.hideSpinner();
    }
  }

  private showSpinner() {
    // Make sure, the spinenr is only added once and only if the element is a button
    if (this.spinnerVisible || !(this.view.rootNodes[0] instanceof HTMLButtonElement)
    ) {
      return;
    }

    // The root node should be the button element
    const rootNode: HTMLButtonElement = this.view.rootNodes[0];

    // Creates a app-spinner that is a sibling of the button
    const spinnerFactory = this.componentFactoryResolver.resolveComponentFactory(SpinnerComponent);
    this.spinnerInstance = this.viewContainerRef.createComponent(spinnerFactory);

    // Save initial styles before overwriting them
    this.initialStyles.position = rootNode.style.position;
    this.initialStyles.opacity = rootNode.style.opacity;
    this.initialStyles.disabled = rootNode.disabled;

    // Make the app-spinner a child component of the button
    this.renderer.appendChild(rootNode, this.spinnerInstance.location.nativeElement);

    // Move the actual button to an invisible position (to keep the position and dimensions of the button for the spinner)
    this.renderer.setStyle(rootNode, 'position', 'relative');
    this.renderer.setStyle(rootNode, 'left', '-9999px');
    this.renderer.setStyle(rootNode, 'top', '-9999px');

    // Some styles can affect the spinner, these styles/attributes must be overwritten
    this.renderer.setStyle(rootNode, 'opacity', '1');
    this.renderer.setProperty(rootNode, 'disabled', true);

    // Let the spinner fill out the whole button (it will center itself automatically)
    // Set the spinner position to the original position of the button
    this.renderer.setStyle(this.spinnerInstance.location.nativeElement, 'position', 'absolute');
    this.renderer.setStyle(this.spinnerInstance.location.nativeElement, 'left', '9999px');
    this.renderer.setStyle(this.spinnerInstance.location.nativeElement, 'top', '9999px');
    this.renderer.setStyle(this.spinnerInstance.location.nativeElement, 'width', '100%');
    this.renderer.setStyle(this.spinnerInstance.location.nativeElement, 'height', '100%');

    this.spinnerVisible = true;
  }

  private hideSpinner() {
    if (!this.spinnerVisible) {
      return;
    }

    const rootNode: HTMLButtonElement = this.view.rootNodes[0];
    this.renderer.removeChild(rootNode, this.spinnerInstance.location.nativeElement);

    // Restore initial styles
    if (this.initialStyles.position) {
      this.renderer.setStyle(rootNode, 'position', this.initialStyles.position);
    } else {
      this.renderer.removeStyle(rootNode, 'position');
    }

    if (this.initialStyles.opacity) {
      this.renderer.setStyle(rootNode, 'opacity', this.initialStyles.opacity);
    } else {
      this.renderer.removeStyle(rootNode, 'opacity');
    }

    if (this.initialStyles.disabled) {
      this.renderer.setProperty(rootNode, 'disabled', true);
    } else {
      this.renderer.setProperty(rootNode, 'disabled', false);
    }

    // We can just remove these styles because we told in the comments that the directive does not support absolute positioning
    this.renderer.removeStyle(rootNode, 'left');
    this.renderer.removeStyle(rootNode, 'top');

    this.spinnerVisible = false;
  }
}

/**
 * Styles/attributes that must be stored because they are overwritten by the BtnLoadingDirective
 */
interface InitialStyles {
  position?: string;
  opacity?: string;
  disabled?: boolean;
}
